import React, { FC, useState } from "react"
import { CardContent, Grid, InputLabel, MenuItem, Select } from "@material-ui/core"

import BaseSnoozeDialog from "@/pages/Attendance/Chat/ConversationPanel/ConversationHeader/NewSnooze/components/BaseSnoozeDialog"
import { Portlet } from "@/components"
import MessageItem from "@/components/ChatMessageBuilder/MessageItem"

import useDidMount from "@/hooks/useDidMount"
import { ErrorType } from "@/hooks/useValidation"

import { getTemplateCategoryChip } from "@/@integrations/WABA/utils/template"

import { WABAChannelMessageTemplate } from "@/@integrations/WABA/protocols/wabaChannelMessageTemplate"

import ErrorHandlerService from "@/services/ErrorHandler"
import AnalyticsService from "@/services/Analytics"
import WABAChannelMessageTemplateService from "@/@integrations/WABA/services/WABAChannelMessageTemplate"

import WhatsappBackground from "@/assets/images/waba/whatsapp_background.png"

import useStyles from "@/pages/Attendance/Chat/ConversationPanel/ConversationHeader/NewSnooze/dialogs/WABATemplateMessageDialog/styles"

type WABATemplateMessageDialogProps = {
	inboxChannelChatId: number
	inboxChannelId: number
	defaultOpen?: boolean
	onClose: () => void
	userId: number
	instanceId: number
}

const WABATemplateMessageDialog: FC<WABATemplateMessageDialogProps> = (props) => {
	const {
		inboxChannelId,
		children,
		defaultOpen = false,
		onClose,
		userId,
		instanceId
	} = props

	const classes = useStyles()

	const [templates, setTemplates] = useState<WABAChannelMessageTemplate[]>([])
	const [wabaChannelMessageTemplateId, setWabaChannelMessageTemplateId] = useState<number | undefined>(undefined)
	const [openWABATemplateMessageDialog, setOpenWABATemplateMessageDialog] = useState<boolean>(defaultOpen)

	const selectedTemplate = templates.find(template => template.id === wabaChannelMessageTemplateId)

	const handleOpenWABATemplateMessageDialog = () => {
		AnalyticsService.capture("waba_snooze_template_opened")
		setOpenWABATemplateMessageDialog(true)
	}

	const handleCloseWABATemplateMessageDialog = () => {
		setOpenWABATemplateMessageDialog(false)

		onClose()
	}

	const getWABATemplates = async (): Promise<void> => {
		try {
			const response = await WABAChannelMessageTemplateService.retrieveAllByInboxChannelId(inboxChannelId)

			setTemplates(response)
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	const handleChangeSelectedTemplate = (templateId: number) => {
		const template = templates.find(template => template.id === templateId)

		if (template) {
			setWabaChannelMessageTemplateId(templateId)
		}
	}

	const isSaveButtonDisabled = React.useMemo(() => {
		const emptyTemplateMessage = !wabaChannelMessageTemplateId

		if (emptyTemplateMessage) {
			return true
		}

		return false
	}, [wabaChannelMessageTemplateId])

	useDidMount(() => {
		getWABATemplates()
	})

	const renderWABATemplateMessageDialog = () => {
		return (
			<BaseSnoozeDialog
				{...props}
				onClose={handleCloseWABATemplateMessageDialog}
				isOpenDialog={openWABATemplateMessageDialog}
				wabaTemplateMessageId={wabaChannelMessageTemplateId}
				snoozeType="waba-message-template"
				isSaveButtonDisabled={isSaveButtonDisabled}
				userId={userId}
				instanceId={instanceId}
			>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Grid container spacing={1}>
							<Grid item xs={12}>
								<InputLabel>Nome do template:</InputLabel>
							</Grid>
							<Grid item xs={12}>
								<Select
									variant="outlined"
									fullWidth
									value={selectedTemplate?.id || ""}
									onChange={({ target }) => handleChangeSelectedTemplate(target.value as number)}
									placeholder="Template"
									displayEmpty={true}
								>
									{templates.map((template, index) => (
										<MenuItem key={index} value={template.id} disabled={template.status !== "approved"}>
											<Grid container alignItems="center" justifyContent="space-between">
												<Grid item>
													{template.name}
												</Grid>

												<Grid item>
													{getTemplateCategoryChip(template.category)}
												</Grid>
											</Grid>
										</MenuItem>
									))}
								</Select>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={12}>
						<Portlet
							style={{
								padding: 4,
								flexDirection: "column",
								justifyContent: "end",
								borderRadius: 8,
								position: "sticky"
							}}
						>
							<Grid container className={classes.messagePreview}>
								<Grid item>
									<CardContent
										className={classes.previewMessageContent}
										style={{
											backgroundImage: `url(${WhatsappBackground})`
										}}
									>
										<MessageItem
											className={classes.container}
											type={"text"}
											content={selectedTemplate?.message?.content}
											extraData={selectedTemplate?.message?.extraData}
											isDefaultSize={false}
											copySpecial={false}
										/>
									</CardContent>
								</Grid>
							</Grid>
						</Portlet>
					</Grid>
				</Grid>
			</BaseSnoozeDialog>
		)
	}

	return (
		<Grid container style={{ width: "100%" }}>
			<Grid onClick={handleOpenWABATemplateMessageDialog} style={{ width: "100%" }}>
				{children}
			</Grid>
			{renderWABATemplateMessageDialog()}
		</Grid>
	)
}

export default WABATemplateMessageDialog
